import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useJobsite } from "../context/JobsiteContext";
import { useAuth } from "../context/AuthContext";
import { getPosition, geoErrorMessage, haversine } from "../lib/geo";
import LocationMap, { SiteLocationSetup, formatDistance } from "../components/LocationMap";
import LunchPicker from "../components/LunchPicker";
import {
  LUNCH_OPTIONS,
  grossSecondsOf,
  fmtTime,
  fmtDur,
  paidSecondsOf as shiftSeconds,
  paidMinutesOf as shiftMinutes,
  grossMinutesOf,
  lunchMinutesOf,
  statusOf,
  getLastLunch,
  saveLastLunch,
} from "../lib/timecard";
import {
  ArrowLeftRight,
  Search,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Clock,
  MapPin,
  Square,
  LogIn,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";

/* ---------- ajudantes de data e tempo ---------- */

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// A semana comeca na segunda-feira.
function startOfWeek(d) {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
}

function fmtDayLong(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "long", year: "numeric" });
}

function fmtDayShort(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function fmtClock(totalSeconds) {
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const s = String(totalSeconds % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function useNow(ms) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/* ---------- localizacao ---------- */

const LUNCH_TOO_LONG = "Lunch time cannot exceed the total hours worked for the day.";

// Minutos inteiros trabalhados (sem arredondar para cima): o almoco precisa caber neles.
function workedWhole(shift, nowMs) {
  return Math.floor(grossSecondsOf(shift, nowMs) / 60);
}

// Mensagem de erro de chamadas ao servidor (inclui o caso de demora demais).
function apiErrorMessage(err, fallback) {
  if (err && err.code === "ECONNABORTED") {
    return "The server is taking too long to answer. Please try again in a moment.";
  }
  const d = err && err.response && err.response.data && err.response.data.detail;
  return typeof d === "string" ? d : fallback;
}

/* ---------- pecas visuais ---------- */

const CHIP_TONES = {
  muted: "border-border bg-muted text-muted-foreground",
  accent: "border-primary/40 bg-primary/10 text-primary",
  ok: "border-green-500/40 bg-green-500/10 text-green-600 dark:text-green-400",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  bad: "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400",
};

function Chip({ tone = "muted", children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${CHIP_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function BottomSheet({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className="relative max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-xl border border-border bg-card shadow-2xl sm:rounded-md"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <button type="button" onClick={onClose} className="text-sm font-medium text-muted-foreground hover:text-foreground">
            Cancel
          </button>
          <div className="text-base font-semibold">{title}</div>
          <span className="w-12" />
        </div>
        <div className="space-y-5 p-5">{children}</div>
      </div>
    </div>
  );
}

function ShiftCard({ s, now, onEdit }) {
  const mins = shiftMinutes(s, now);
  const lunch = lunchMinutesOf(s);
  const outside = s.within_geofence === false || s.out_within_geofence === false;
  const status = statusOf(s);
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid={`shift-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-base font-semibold">{s.jobsite_name || "Jobsite"}</div>
          <div className="mt-1 font-mono text-sm text-muted-foreground">
            {fmtTime(s.clock_in)} - {s.clock_out ? fmtTime(s.clock_out) : "now"}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-xl font-bold">{fmtDur(mins)}</div>
          <div className="font-mono text-[11px] text-muted-foreground">{lunch > 0 ? `lunch ${lunch} min` : "no lunch"}</div>
        </div>
      </div>

      {s.note && <div className="mt-2 text-sm text-muted-foreground">“{s.note}”</div>}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {status === "active" && <Chip tone="accent">Active</Chip>}
        {status === "pending" && <Chip tone="warn">Awaiting approval</Chip>}
        {status === "approved" && <Chip tone="ok">Approved</Chip>}
        {status === "rejected" && <Chip tone="bad">Rejected</Chip>}
        {s.edited && <Chip tone="accent">Edited by {s.edited_by || "supervisor"}</Chip>}
        {s.location_missing ? (
          <Chip tone="warn">No location</Chip>
        ) : outside ? (
          <Chip tone="warn">Outside geofence</Chip>
        ) : s.within_geofence === true ? (
          <Chip tone="ok">Geo OK</Chip>
        ) : null}
      </div>

      {status === "rejected" && (
        <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm">
          <div className="font-semibold">{s.rejected_by || "Your supervisor"} sent this back</div>
          {s.reject_reason && <div className="mt-0.5 text-muted-foreground">{s.reject_reason}</div>}
        </div>
      )}
      {status === "approved" && s.approved_by && (
        <div className="mt-2 text-xs text-muted-foreground">Approved by {s.approved_by}</div>
      )}

      {(status === "pending" || status === "rejected") && (
        <button
          type="button"
          onClick={() => onEdit(s)}
          className="mt-3 h-11 w-full rounded-md border border-border text-sm font-semibold active:bg-accent"
          data-testid={`edit-shift-${s.id}`}
        >
          {status === "rejected" ? "Fix and send again" : "Edit lunch or note"}
        </button>
      )}
    </div>
  );
}

/* ---------- pagina ---------- */

export default function AttendanceMobile() {
  const jobsite = useJobsite();
  const { sites, activeSite } = jobsite;
  const { user } = useAuth();
  const canEditSite = user?.role === "admin" || user?.role === "supervisor";
  const [view, setView] = useState("day");
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const [sheet, setSheet] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [geo, setGeo] = useState({ state: "idle" });
  const [busy, setBusy] = useState(false);
  // Clock out: almoco escolhido e descricao
  const [lunchChoice, setLunchChoice] = useState(30);
  const [noteText, setNoteText] = useState("");
  // Editar um turno ja enviado (antes de ser aprovado)
  const [editing, setEditing] = useState(null);
  const [editLunch, setEditLunch] = useState(0);
  const [editNote, setEditNote] = useState("");
  const [savingDetails, setSavingDetails] = useState(false);
  // Local da obra recem-definido (vale na hora, antes da lista de obras recarregar).
  const [siteOverrides, setSiteOverrides] = useState({});
  // Local encontrado pelo endereco, ainda nao salvo (so aparece no mapa).
  const [candidate, setCandidate] = useState(null);
  // Switch task (trocar de obra sem sair do turno)
  const [switchQuery, setSwitchQuery] = useState("");
  const [switchSort, setSwitchSort] = useState("closest");
  const [switchTarget, setSwitchTarget] = useState(null);
  const [switchLunch, setSwitchLunch] = useState(0);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [here, setHere] = useState(null);

  const now = useNow(current ? 1000 : 30000);

  const load = useCallback(async () => {
    try {
      const [cur, hist] = await Promise.all([
        api.get("/attendance/current"),
        api.get("/attendance/history", { params: { limit: 300 } }),
      ]);
      setCurrent(cur.data || null);
      setHistory(Array.isArray(hist.data) ? hist.data : []);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error("Could not load your timecards.");
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* intervalo mostrado */
  const rangeStart = view === "week" ? startOfWeek(cursor) : startOfDay(cursor);
  const rangeEnd = addDays(rangeStart, view === "week" ? 7 : 1);
  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();

  const shifts = useMemo(
    () =>
      history.filter((s) => {
        const t = Date.parse(s.clock_in);
        return t >= rangeStartMs && t < rangeEndMs && (!current || s.id !== current.id);
      }),
    [history, current, rangeStartMs, rangeEndMs]
  );

  const currentInRange = current
    ? Date.parse(current.clock_in) >= rangeStartMs && Date.parse(current.clock_in) < rangeEndMs
    : false;

  const totalMins =
    shifts.reduce((acc, s) => acc + shiftMinutes(s, now), 0) + (currentInRange ? shiftMinutes(current, now) : 0);

  const dayGroups = useMemo(() => {
    const days = Array.from({ length: view === "week" ? 7 : 1 }, (_, i) => addDays(rangeStart, i));
    return days
      .map((d) => ({
        date: d,
        items: shifts.filter((s) => new Date(s.clock_in).toDateString() === d.toDateString()),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shifts, view, rangeStartMs]);

  const step = view === "week" ? 7 : 1;
  const dateLabel =
    view === "week"
      ? `${fmtDayShort(rangeStart)} - ${fmtDayShort(addDays(rangeStart, 6))}`
      : fmtDayLong(cursor);

  /* turno aberto */
  const elapsedMs = current ? now - Date.parse(current.clock_in) : 0;
  const stale = elapsedMs > 16 * 3600 * 1000;
  const lunchTooLong = !!current && lunchChoice > workedWhole(current, now);
  const editTooLong = !!editing && editLunch > workedWhole(editing, now);
  const switchTooLong = !!current && switchLunch > workedWhole(current, now);

  /* localizacao */
  const locate = useCallback(async () => {
    setGeo({ state: "loading" });
    try {
      const p = await getPosition(8000);
      setGeo({ state: "ok", ...p });
    } catch (e) {
      setGeo({ state: "error", message: geoErrorMessage(e), detail: e && e.detail });
    }
  }, []);

  const openClockIn = () => {
    const first = (activeSite && activeSite.id) || (sites[0] && sites[0].id) || "";
    setProjectId(first);
    setCandidate(null);
    setSheet("in");
    locate();
  };

  const closeSheet = useCallback(() => {
    if (!busy) setSheet(null);
  }, [busy]);

  const baseSite = sites.find((s) => s.id === projectId) || null;
  const selectedSite = baseSite ? { ...baseSite, ...(siteOverrides[baseSite.id] || {}) } : null;
  const siteHasCoords = !!(selectedSite && selectedSite.lat && selectedSite.lng);
  const mapSite = candidate
    ? { lat: candidate.lat, lng: candidate.lng, radius_m: selectedSite?.radius_m || 200, name: selectedSite?.name }
    : siteHasCoords
    ? { lat: selectedSite.lat, lng: selectedSite.lng, radius_m: selectedSite.radius_m || 200, name: selectedSite.name }
    : null;

  const handleSiteSaved = (point) => {
    if (!selectedSite) return;
    setSiteOverrides((prev) => ({ ...prev, [selectedSite.id]: point }));
    setCandidate(null);
    // Pede para a lista de obras recarregar, se o app tiver essa funcao.
    const reload = jobsite.refresh || jobsite.reload || jobsite.refetch;
    if (typeof reload === "function") reload();
  };

  let siteDistance = null;
  let siteRadius = 200;
  let insideFence = null;
  if (selectedSite && geo.state === "ok" && selectedSite.lat && selectedSite.lng) {
    siteDistance = haversine(geo.lat, geo.lng, selectedSite.lat, selectedSite.lng);
    siteRadius = selectedSite.radius_m || 200;
    insideFence = siteDistance <= siteRadius;
  }

  /* acoes */
  const confirmClockIn = async (withoutLocation = false) => {
    if (!projectId) return;
    if (!withoutLocation && geo.state !== "ok") return;
    setBusy(true);
    try {
      const payload = { jobsite_id: projectId };
      if (!withoutLocation) {
        payload.lat = geo.lat;
        payload.lng = geo.lng;
      }
      await api.post("/attendance/clock-in", payload, { timeout: 25000 });
      toast.success("Clocked in");
      setSheet(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not clock in."));
    } finally {
      setBusy(false);
    }
  };

  const confirmClockOut = async () => {
    if (!current) return;
    if (lunchChoice > workedWhole(current, Date.now())) {
      toast.error(LUNCH_TOO_LONG);
      return;
    }
    setBusy(true);
    // A localizacao e so um extra: se nao vier, encerra o turno mesmo assim.
    let p = null;
    try {
      p = await getPosition(4000);
    } catch (e) {
      p = null;
    }
    try {
      const body = { jobsite_id: current.jobsite_id, lunch_minutes: lunchChoice };
      if (noteText.trim()) body.note = noteText.trim();
      if (p) {
        body.lat = p.lat;
        body.lng = p.lng;
      }
      await api.post("/attendance/clock-out", body, { timeout: 25000 });
      saveLastLunch(lunchChoice);
      toast.success("Clocked out. Your timecard is waiting for approval.");
      setSheet(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not clock out. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const openClockOut = () => {
    // O ultimo almoco vira o padrao, mas so se couber no tempo ja trabalhado.
    const worked = current ? workedWhole(current, Date.now()) : 0;
    const last = getLastLunch();
    const fitting = LUNCH_OPTIONS.filter((o) => o <= worked);
    setLunchChoice(last <= worked ? last : Math.max(...fitting));
    setNoteText("");
    setSheet("out");
  };

  const openEdit = (shift) => {
    setEditing(shift);
    setEditLunch(typeof shift.lunch_minutes === "number" ? shift.lunch_minutes : lunchMinutesOf(shift));
    setEditNote(shift.note || "");
  };

  const saveDetails = async () => {
    if (!editing) return;
    setSavingDetails(true);
    try {
      await api.patch(
        `/attendance/${editing.id}/details`,
        { lunch_minutes: editLunch, note: editNote },
        { timeout: 25000 }
      );
      toast.success(editing.approved === false ? "Sent again for approval" : "Timecard updated");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update the timecard."));
    } finally {
      setSavingDetails(false);
    }
  };

  const locateHere = useCallback(async () => {
    try {
      setHere(await getPosition(4000));
    } catch (e) {
      setHere(null);
    }
  }, []);

  const openSwitch = () => {
    setSwitchQuery("");
    setSwitchSort("closest");
    setSwitchTarget(null);
    setSwitchLunch(0);
    setSheet("switch");
    locateHere();
  };

  const closeSwitch = () => {
    if (switchBusy) return;
    setSheet(null);
    setSwitchTarget(null);
  };

  const effectiveSort = switchSort === "closest" && here ? "closest" : "name";

  const switchList = useMemo(() => {
    const q = switchQuery.trim().toLowerCase();
    const rows = sites
      .map((st) => {
        const site = { ...st, ...(siteOverrides[st.id] || {}) };
        const hasCoords = !!(site.lat && site.lng);
        const distance = here && hasCoords ? haversine(here.lat, here.lng, site.lat, site.lng) : null;
        return { site, distance };
      })
      .filter(({ site }) => !q || (site.name || "").toLowerCase().includes(q) || (site.address || "").toLowerCase().includes(q));
    const byName = (a, b) => (a.site.name || "").localeCompare(b.site.name || "");
    if (effectiveSort === "closest") {
      rows.sort((a, b) => (a.distance === null ? Infinity : a.distance) - (b.distance === null ? Infinity : b.distance) || byName(a, b));
    } else {
      rows.sort(byName);
    }
    return rows;
  }, [sites, siteOverrides, here, switchQuery, effectiveSort]);

  const confirmSwitch = async () => {
    if (!switchTarget || !current) return;
    if (switchLunch > workedWhole(current, Date.now())) {
      toast.error(LUNCH_TOO_LONG);
      return;
    }
    setSwitchBusy(true);
    try {
      let p = here;
      if (!p) {
        try {
          p = await getPosition(4000);
        } catch (e) {
          p = null;
        }
      }
      const body = { jobsite_id: switchTarget.id, lunch_minutes: switchLunch };
      if (p) {
        body.lat = p.lat;
        body.lng = p.lng;
      }
      await api.post("/attendance/switch", body, { timeout: 25000 });
      toast.success(`Switched to ${switchTarget.name}`);
      setSheet(null);
      setSwitchTarget(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not switch. Please try again."));
    } finally {
      setSwitchBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-5">
      {/* Titulo */}
      <div className="mb-5">
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">My Time</h1>
        <p className="mt-1 text-sm text-muted-foreground">GPS verified clock in and out. You choose your lunch time when you clock out.</p>
      </div>

      {/* Dia / Semana */}
      <div className="grid grid-cols-2 rounded-md border border-border bg-muted p-1" role="tablist" aria-label="View">
        {["day", "week"].map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`rounded-sm py-3 text-base font-semibold capitalize transition-colors ${
              view === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`view-${v}`}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Navegador de data */}
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCursor(addDays(cursor, -step))}
          aria-label="Previous"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-md border border-border bg-card active:bg-accent"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setCursor(startOfDay(new Date()))}
          className="flex h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-md px-2 text-primary active:bg-accent"
          title="Go to today"
        >
          <CalendarDays className="h-5 w-5 shrink-0" />
          <span className="truncate text-base font-semibold">{dateLabel}</span>
        </button>
        <button
          type="button"
          onClick={() => setCursor(addDays(cursor, step))}
          aria-label="Next"
          className="grid h-12 w-12 shrink-0 place-items-center rounded-md border border-border bg-card active:bg-accent"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{updatedAt ? `Last updated ${updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Loading..."}</span>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 hover:text-foreground">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Turno em andamento */}
      {current && (
        <div className="relative mt-5 overflow-hidden rounded-md border border-primary/40 bg-card p-5" data-testid="current-shift">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent" />
          <div className="relative">
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                </span>
                On the clock
              </div>
              {current.location_missing ? (
                <Chip tone="warn">No location</Chip>
              ) : current.within_geofence === false ? (
                <Chip tone="warn">Outside geofence</Chip>
              ) : (
                <Chip tone="ok">Geo OK</Chip>
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <Clock className="h-8 w-8 shrink-0 text-primary" />
              <div className="font-mono text-4xl font-bold tracking-tight md:text-5xl">
                {elapsedMs >= 24 * 3600 * 1000
                  ? fmtDur(shiftMinutes(current, now))
                  : fmtClock(shiftSeconds(current, now))}
              </div>
            </div>

            <div className="mt-3 text-base font-semibold">{current.jobsite_name}</div>
            <div className="mt-0.5 font-mono text-sm text-muted-foreground">
              Started {fmtTime(current.clock_in)}
            </div>
          </div>
        </div>
      )}

      {/* Aviso: turno esquecido aberto */}
      {current && stale && (
        <div className="mt-3 flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            This shift has been open for {fmtDur(Math.floor(elapsedMs / 60000))}. If you forgot to clock out, clock out now and ask an
            admin to review the hours.
          </div>
        </div>
      )}

      {/* Resumo e lista */}
      {loaded && (shifts.length > 0 || currentInRange) && (
        <div className="mt-6 flex items-center justify-between border-b border-border pb-2">
          <div className="label-overline">{view === "week" ? "Week total" : "Day total"}</div>
          <div className="font-mono text-lg font-bold">{fmtDur(totalMins)}</div>
        </div>
      )}

      <div className="mt-4 space-y-5">
        {dayGroups.map((g) => (
          <div key={g.date.toDateString()}>
            {view === "week" && (
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">{fmtDayShort(g.date)}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {fmtDur(g.items.reduce((acc, s) => acc + shiftMinutes(s, now), 0))}
                </div>
              </div>
            )}
            <div className="space-y-3">
              {g.items.map((s) => (
                <ShiftCard key={s.id} s={s} now={now} onEdit={openEdit} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {loaded && shifts.length === 0 && !currentInRange && !current && (
        <div className="grid place-items-center py-20 text-center">
          <div className="text-3xl font-bold tracking-tight">No timecards</div>
          <div className="mt-2 text-sm text-muted-foreground">Tap Clock In to start your day.</div>
        </div>
      )}

      {/* Barra de acao fixa, logo acima da barra de abas do celular */}
      <div
        className="fixed inset-x-0 z-30 border-t border-border bg-background/95 backdrop-blur"
        style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-3xl gap-3 px-4 py-3">
          {current ? (
            <>
              <button
                type="button"
                onClick={openSwitch}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-md border border-border bg-card text-base font-semibold hover:bg-accent"
                data-testid="switch-task-btn"
              >
                <ArrowLeftRight className="h-5 w-5" /> Switch task
              </button>
              <button
                type="button"
                onClick={openClockOut}
                className="flex h-14 flex-1 items-center justify-center gap-2 rounded-md bg-destructive text-base font-semibold text-destructive-foreground hover:bg-destructive/90"
                data-testid="clock-out-btn"
              >
                <Square className="h-5 w-5" /> Clock out
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={openClockIn}
              className="flex h-14 flex-1 items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90"
              data-testid="clock-in-btn"
            >
              <LogIn className="h-5 w-5" /> Clock in
            </button>
          )}
        </div>
      </div>

      {/* Folha: Clock in */}
      {sheet === "in" && (
        <BottomSheet title="Clock in" onClose={closeSheet}>
          <div>
            <label htmlFor="clock-in-project" className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Project (required)
            </label>
            {sites.length === 0 ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                No jobsites available. Ask an admin to add you to a jobsite.
              </div>
            ) : (
              <select
                id="clock-in-project"
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setCandidate(null);
                }}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base outline-none focus:border-primary"
                data-testid="clock-in-project"
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="rounded-md border border-border bg-background p-4" data-testid="clock-in-location">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> Location
            </div>
            {geo.state === "loading" && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Getting your location...
              </div>
            )}
            {geo.state === "ok" && (
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" /> Location found (about {Math.round(geo.accuracy || 0)} m)
                </div>
                {siteDistance !== null && (
                  <div className={insideFence ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}>
                    {formatDistance(siteDistance)} from the site center, {insideFence ? "inside" : "outside"} the {siteRadius} m geofence.
                    {!insideFence && " Your clock in will be flagged."}
                  </div>
                )}
              </div>
            )}
            {geo.state === "error" && (
              <div className="mt-2 space-y-3">
                <div className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {geo.message}
                </div>
                {geo.detail && (
                  <div className="break-words font-mono text-[11px] text-muted-foreground">Details: {geo.detail}</div>
                )}
                <button
                  type="button"
                  onClick={locate}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-accent"
                >
                  <RefreshCw className="h-4 w-4" /> Try again
                </button>
              </div>
            )}
          </div>

          {geo.state === "ok" && (
            <div className="space-y-3">
              <LocationMap user={geo} site={mapSite} />
              {!siteHasCoords && selectedSite && (
                <SiteLocationSetup
                  site={selectedSite}
                  pos={geo}
                  canEdit={canEditSite}
                  onCandidate={setCandidate}
                  onSaved={handleSiteSaved}
                />
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => confirmClockIn(false)}
            disabled={busy || !projectId || geo.state !== "ok"}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="clock-in-confirm"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />}
            {busy ? "Clocking in..." : "Clock in"}
          </button>

          {geo.state === "error" && (
            <div>
              <button
                type="button"
                onClick={() => confirmClockIn(true)}
                disabled={busy || !projectId}
                className="flex h-12 w-full items-center justify-center rounded-md border border-border text-base font-semibold disabled:opacity-50"
                data-testid="clock-in-no-location"
              >
                Clock in without location
              </button>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Your supervisor will see this clock in marked as “No location”.
              </p>
            </div>
          )}
        </BottomSheet>
      )}

      {/* Folha: Clock out */}
      {sheet === "out" && current && (
        <BottomSheet title="Clock out" onClose={closeSheet}>
          <div className="rounded-md border border-border bg-background p-4">
            <div className="text-base font-semibold">{current.jobsite_name}</div>
            <div className="mt-1 font-mono text-sm text-muted-foreground">Started {fmtTime(current.clock_in)}</div>
          </div>

          <div>
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Lunch time</div>
            <LunchPicker value={lunchChoice} onChange={setLunchChoice} disabled={busy} />
          </div>

          {lunchTooLong && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" role="alert" data-testid="lunch-too-long">
              {LUNCH_TOO_LONG}
            </div>
          )}

          <div className="rounded-md border border-border bg-background p-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Time on site</span>
              <span className="font-mono">{fmtDur(grossMinutesOf(current, now))}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm text-muted-foreground">
              <span>Lunch</span>
              <span className="font-mono">- {lunchChoice} min</span>
            </div>
            <div className="mt-3 flex items-end justify-between border-t border-border pt-3">
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Paid time</span>
              <span className="font-mono text-2xl font-bold" data-testid="paid-preview">
                {fmtDur(Math.max(0, grossMinutesOf(current, now) - lunchChoice))}
              </span>
            </div>
          </div>

          <div>
            <label htmlFor="clock-out-note" className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Description (optional)
            </label>
            <textarea
              id="clock-out-note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="What did you work on today?"
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            After you clock out, your timecard waits for a supervisor or admin to approve it. They can also adjust the hours.
          </p>

          <button
            type="button"
            onClick={confirmClockOut}
            disabled={busy || lunchTooLong}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-destructive text-base font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            data-testid="clock-out-confirm"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Square className="h-5 w-5" />}
            {busy ? "Clocking out..." : "Clock out"}
          </button>
        </BottomSheet>
      )}

      {/* Folha: editar um turno ja enviado */}
      {editing && (
        <BottomSheet title="Timecard details" onClose={() => !savingDetails && setEditing(null)}>
          <div className="rounded-md border border-border bg-background p-4">
            <div className="text-base font-semibold">{editing.jobsite_name}</div>
            <div className="mt-1 font-mono text-sm text-muted-foreground">
              {fmtTime(editing.clock_in)} - {fmtTime(editing.clock_out)}
            </div>
          </div>

          <div>
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Lunch time</div>
            <LunchPicker value={editLunch} onChange={setEditLunch} disabled={savingDetails} />
          </div>

          {editTooLong && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
              {LUNCH_TOO_LONG}
            </div>
          )}

          <div className="flex items-end justify-between rounded-md border border-border bg-background p-4">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Paid time</span>
            <span className="font-mono text-2xl font-bold">
              {fmtDur(Math.max(0, grossMinutesOf(editing, now) - editLunch))}
            </span>
          </div>

          <div>
            <label htmlFor="edit-note" className="mb-1.5 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Description
            </label>
            <textarea
              id="edit-note"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-3 text-base outline-none focus:border-primary"
            />
          </div>

          <button
            type="button"
            onClick={saveDetails}
            disabled={savingDetails || editTooLong}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            data-testid="save-details"
          >
            {savingDetails && <Loader2 className="h-5 w-5 animate-spin" />}
            {editing.approved === false ? "Send again for approval" : "Apply"}
          </button>
        </BottomSheet>
      )}

      {/* Folha: Switch task - escolher a obra */}
      {sheet === "switch" && current && !switchTarget && (
        <BottomSheet title="Select project" onClose={closeSwitch}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={switchQuery}
              onChange={(e) => setSwitchQuery(e.target.value)}
              placeholder="Search"
              className="h-12 w-full rounded-md border border-input bg-background pl-11 pr-4 text-base outline-none focus:border-primary"
              data-testid="switch-search"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const next = switchSort === "closest" ? "name" : "closest";
                setSwitchSort(next);
                if (next === "closest" && !here) locateHere();
              }}
              className="flex h-11 items-center gap-2 rounded-md border border-primary/60 bg-primary/10 px-3 text-sm font-semibold text-primary"
              data-testid="switch-sort"
            >
              {effectiveSort === "closest" ? "Sort: Closest to me" : "Sort: Name"} <span aria-hidden="true">▾</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setSwitchQuery("");
                setSwitchSort("closest");
              }}
              className="h-11 rounded-md border border-border px-3 text-sm font-semibold"
            >
              Clear filters
            </button>
          </div>

          {switchSort === "closest" && !here && (
            <p className="text-xs text-muted-foreground">Location is not available, so the projects are sorted by name.</p>
          )}

          <div className="-mx-5 divide-y divide-border border-t border-border">
            {switchList.length === 0 && <div className="px-5 py-8 text-center text-sm text-muted-foreground">No projects found.</div>}
            {switchList.map(({ site, distance }, i) => {
              const isCurrent = site.id === current.jobsite_id;
              const closest = effectiveSort === "closest" && i === 0 && distance !== null;
              return (
                <button
                  key={site.id}
                  type="button"
                  disabled={isCurrent}
                  onClick={() => {
                    setSwitchTarget(site);
                    setSwitchLunch(0);
                  }}
                  className="block w-full px-5 py-4 text-left active:bg-accent disabled:opacity-50"
                  data-testid={`switch-site-${site.id}`}
                >
                  <div className="text-base font-bold leading-snug">{site.name}</div>
                  {site.address && <div className="mt-0.5 text-sm text-muted-foreground">{site.address}</div>}
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                    {isCurrent && <Chip tone="accent">Current</Chip>}
                    {distance !== null ? <span>{formatDistance(distance)}</span> : <span>No map location</span>}
                    {closest && !isCurrent && (
                      <span className="inline-flex items-center gap-1 text-green-500">
                        <MapPin className="h-4 w-4" /> Closest to you
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </BottomSheet>
      )}

      {/* Folha: Switch task - confirmar a troca */}
      {sheet === "switch" && current && switchTarget && (
        <BottomSheet title="Switch task" onClose={closeSwitch}>
          <button
            type="button"
            onClick={() => setSwitchTarget(null)}
            disabled={switchBusy}
            className="-mt-1 text-sm font-semibold text-primary"
          >
            Back to projects
          </button>

          <div className="rounded-md border border-border bg-background p-4">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Ends</div>
            <div className="text-base font-semibold">{current.jobsite_name}</div>
            <div className="mt-1 font-mono text-sm text-muted-foreground">
              Started {fmtTime(current.clock_in)} | {fmtDur(grossMinutesOf(current, now))} so far
            </div>
            <div className="my-3 border-t border-border" />
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Starts</div>
            <div className="text-base font-semibold">{switchTarget.name}</div>
            {switchTarget.address && <div className="mt-0.5 text-sm text-muted-foreground">{switchTarget.address}</div>}
          </div>

          <div>
            <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              Lunch time (taken at {current.jobsite_name})
            </div>
            <LunchPicker value={switchLunch} onChange={setSwitchLunch} disabled={switchBusy} />
          </div>

          {switchTooLong && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" role="alert" data-testid="switch-lunch-too-long">
              {LUNCH_TOO_LONG}
            </div>
          )}

          <div className="flex items-end justify-between rounded-md border border-border bg-background p-4">
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Paid time here</span>
            <span className="font-mono text-2xl font-bold">
              {fmtDur(Math.max(0, grossMinutesOf(current, now) - switchLunch))}
            </span>
          </div>

          <p className="text-xs text-muted-foreground">
            This ends your current timecard, which goes to approval, and starts a new one at {switchTarget.name}.
          </p>

          <button
            type="button"
            onClick={confirmSwitch}
            disabled={switchBusy || switchTooLong}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="confirm-switch"
          >
            {switchBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowLeftRight className="h-5 w-5" />}
            {switchBusy ? "Switching..." : "Switch task"}
          </button>
        </BottomSheet>
      )}
    </div>
  );
}
